import { JsonFlowchartNode } from './flowchartToGraph';
import constants from '../constants/jsonXMLConstants';
import settings from '../constants/jsonXMLsettings';
import { getOrCreateNoopSessionManagementTP, JE_ParsingContext } from './jsonFQContext';

export interface NodeFieldsRef {
  nodeId: string;
  fieldPaths: string[];
}

export interface TemplatizationField {
  id: string;
  name: string;
  nodeFields: { [nodeId: string]: NodeFieldsRef };
}

export interface TemplatizationEnvironment {
  name: string;
  settings: { [fieldId: string]: string };
}

export interface TemplatizationInfo {
  templateFields: { [index: string]: TemplatizationField };
  environments: { [index: string]: TemplatizationEnvironment };
}

export interface XmlGenerationSettings {
  enablePolicyWideInstrumentation: boolean;
  internallyLinkErrorPagesToTokenIssuer: boolean;
  enableJourneyInsights: boolean;
  enableJavascript: boolean;
  basePolicyId: string;
  addRandomPolicyIdTail: boolean;
  targetEnvironment?: string;
}

export interface EditorSettings {
  performAutoUpload: boolean;
  runNowSamlDeploymentUri: string;
}

export interface BuildSettings {
  templatization?: TemplatizationInfo,
  editorSettings: EditorSettings;
  xmlGenerationSettings: XmlGenerationSettings;
}

export const getTenantSettings = () => {
  return settings.tenantSettings[settings.target as keyof typeof settings.tenantSettings];
};

export const getTemplatizationDefaults = () => {
  return settings.tenantSettings.tenantAgnostic;
}

export const getLinkID: (node: JsonFlowchartNode) => string = (node: JsonFlowchartNode) => {
  if (node.isLink) {
    return node.linkType ? node.linkType : 'unknownLink';
  } else {
    return 'localAccount';
  }
};

export const getNextValidID = (proposedId: string, idSet: Set<string>) => {
  let idIterCount = 0;
  let attemptedId = proposedId;
  while (idSet.has(attemptedId.toLowerCase())) {
    attemptedId = proposedId + `_${idIterCount}`;
    idIterCount += 1;
  }
  return attemptedId;
};

export const sanitizeDisplayName = (name: string) => {
  return name.replaceAll(' ', '_');
};

export interface JE_ProviderInfo {
  providerDisplayName: string;
  profileDisplayName: string;
  domain?: string;
}

export const linkInfo: { [index: string]: JE_ProviderInfo } = {
  localAccount: { providerDisplayName: 'Local Account', profileDisplayName: 'Local Account' },
  facebook: { providerDisplayName: 'Facebook', profileDisplayName: 'Facebook', domain: 'facebook.com' },
  google: { providerDisplayName: 'Google', profileDisplayName: 'Google', domain: 'google.com' },
  linkedIn: { providerDisplayName: 'LinkedIn', profileDisplayName: 'LinkedIn', domain: 'linkedin.com' },
  weChat: { providerDisplayName: 'WeChat (Preview)', profileDisplayName: 'WeChat', domain: 'wechat.com' },
  forgotPassword: { providerDisplayName: 'Self Asserted', profileDisplayName: 'Forgot Password' },
  createNewAccount: { providerDisplayName: 'Self Asserted', profileDisplayName: 'Sign Up' },
  employeeLogin: { providerDisplayName: 'Self Asserted', profileDisplayName: 'Employee Login' },
  otp: { providerDisplayName: 'Self Asserted', profileDisplayName: 'OTP' },
};

export const nameToPolicyId = (policyName: string) => {
  let updatedName = policyName.replaceAll(' ', '_');
  if (!updatedName.startsWith('B2C_1A_')) {
    return `B2C_1A_${updatedName}`;
  } else {
    return updatedName;
  }
};

// Parse a string or boolean value to a boolean.
// For values that are already booleans, returns the same value.
// For strings, returns true if the string is 'true' and false otherwise.
// For other values, will always return false.
export const parseToBoolean = (value: any) => {
  return value === true || value === 'true';
};

// Resolver handling helpers.

// Distinguish real resolver from special ("pseudo-resolvers" that are manually implemented by claims transforms etc) resolvers.
export const isRealResolver = (resolver: string) => {
  return isStandardResolver(resolver) || isCustomKeyResolver(resolver);
};

export const isStandardResolver = (resolver: string) => {
  return constants.sessionContexts.resolvers.hasOwnProperty(resolver);
};

export const isCustomKeyResolver = (resolver: string) => {
  return Object.keys(constants.sessionContexts.customKeyResolvers).some((k) =>
    resolver.startsWith(
      constants.sessionContexts.customKeyResolvers[k as keyof typeof constants.sessionContexts.customKeyResolvers]
        .prefix,
    ),
  );
};

export const getRealResolverInfo = (resolver: string) => {
  if (isStandardResolver(resolver)) {
    return constants.sessionContexts.resolvers[resolver as keyof typeof constants.sessionContexts.resolvers];
  } else if (isCustomKeyResolver(resolver)) {
    let matchKey = Object.keys(constants.sessionContexts.customKeyResolvers).find((key) => {
      return resolver.startsWith(
        constants.sessionContexts.customKeyResolvers[key as keyof typeof constants.sessionContexts.customKeyResolvers]
          .prefix,
      );
    });
    if (matchKey) {
      return constants.sessionContexts.customKeyResolvers[
        matchKey as keyof typeof constants.sessionContexts.customKeyResolvers
      ];
    }
  }
};

export const isSpecialResolver = (resolver: string) => {
  return constants.sessionContexts.specials.hasOwnProperty(resolver);
};

export const getNodeSessionProfileId = (node: JsonFlowchartNode, parsingCtx: JE_ParsingContext) => {
  if (node.sessionBehavior && node.sessionBehavior.sessionAction === constants.sessionActions.globalSSO) {
    return parsingCtx.sessions['_SSO'].sessionProfileId;
  } else {
    return getOrCreateNoopSessionManagementTP(parsingCtx).id;
  }
};

const parseTemplatizationInfo: (templatizationInfo: any) => TemplatizationInfo = (templatizationInfo: any) => {
  return {
    templateFields: Object.keys(templatizationInfo.templateFields).reduce((tFields: { [index: string]: TemplatizationField }, fieldId) => {
      tFields[fieldId] = {
        id: fieldId,
        name: templatizationInfo.templateFields[fieldId].name,
        nodeFields: Object.keys(templatizationInfo.templateFields[fieldId].nodeFields).reduce((nFields: { [index: string]: NodeFieldsRef }, fId) => {
          nFields[fId] = {
            nodeId: templatizationInfo.templateFields[fieldId].nodeFields[fId].nodeId,
            fieldPaths: [ ...templatizationInfo.templateFields[fieldId].nodeFields[fId].fieldPaths]
          };
          return nFields;
        }, {})
      }
      return tFields;
    }, {}),
    environments: Object.keys(templatizationInfo.environments).reduce((envs: { [index: string]: TemplatizationEnvironment }, envId) => {
      envs[envId] = {
        name: templatizationInfo.environments[envId].name,
        settings: { ...templatizationInfo.environments[envId].settings }
      }
      return envs;
    }, {})
  };
}

export const initSettings = (otherSettingsJson?: { [index: string]: any }, templatizationInfo?: any) => {
  let editorSettings: EditorSettings = {
    performAutoUpload: getTenantSettings().performAutoUpload,
    runNowSamlDeploymentUri: settings.runNowSamlDeploymentUri
  };
  let generatorSettings: XmlGenerationSettings = {
    enablePolicyWideInstrumentation: settings.enablePolicyWideInstrumentation,
    internallyLinkErrorPagesToTokenIssuer: settings.internallyLinkErrorPagesToTokenIssuer,
    enableJourneyInsights: settings.enableGeneratedApplicationInsights,
    addRandomPolicyIdTail: true,
    basePolicyId: 'B2C_1A_PolicyBase', // Todo: expose this and other editor settings in UI
    enableJavascript: true
  }
  if (otherSettingsJson) {
    generatorSettings = { ...generatorSettings, ...otherSettingsJson };
  }
  let settingObj: BuildSettings = {
    editorSettings: editorSettings,
    xmlGenerationSettings: generatorSettings,
    templatization: templatizationInfo ? parseTemplatizationInfo(templatizationInfo) : undefined,
  }
  return settingObj;
}

// Extract XML generation settings from editor state.
export const extractXMLGenerationSettings = (editorState: any) => {
  // TODO: Build a frontend portal for controlling the XML generation settings.
  return {}
}

// Fetch a default/expected settings value (e.g. a templatizable field not exposed by the editor).
export const extractSettingFieldValue = (field: string, buildSettings: BuildSettings) => {
  return extractTemplatizableField({ value: field, templatized: true }, buildSettings);
}

// Get the value corresponding to a JSON field that may be in templatizable format or templatized.
export const extractTemplatizableField = (field: any, buildSettings: BuildSettings) => {
  if (!field) {
    // Let empty values just be treated as empty values.
    return field;
  }
  if ((!buildSettings.templatization) || (typeof (field) === 'string')) {
    // This is expressed in the JSON as a non-templatizable field, possibly due to being from JSON generated prior to a given field becoming templatizable.
    // For backwards compatability, just treat its value as its literal contents.
    return field;
  } else if (!field.templatized) {
    // The field is templatizable but marked non-templatized (uses a literal value instead.)
    return field.value;
  } else if (buildSettings.xmlGenerationSettings.targetEnvironment && Object.keys(buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings).includes(field.value) && (!!buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings[field.value])) {
    // The policy build is targeting a specific environment, and that environment defines a nonempty value for the setting corresponding to this templatized field.
    // Return the value from the environment definition.
    return buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings[field.value];
  } else {
    // The field should be templatized. Return the template-tag format for the field.
    return `{settings:${buildSettings.templatization.templateFields[field.value].name}}`;
  }
}

interface AppSettingsEnvironment {
  Name: string,
  Production: boolean,
  Tenant: string,
  PolicySettings: { [index: string]: string }
}

export const generateAppSettingsJsonString = (templatization: any, envsToInclude?: string[]) => {
  let appSettings: { Environments: AppSettingsEnvironment[] } = { Environments: [] };
  Object.keys(templatization.environments).forEach((envId: string) => {
    if ((!envsToInclude) || (envsToInclude.includes(envId))) {
      let envRef: AppSettingsEnvironment = {
        Name: templatization.environments[envId].name,
        Production: false,
        Tenant: templatization.environments[envId].settings['Tenant'] ? templatization.environments[envId].settings['Tenant'] : "",
        PolicySettings: {}
      };
      appSettings.Environments.push(envRef);
      Object.keys(templatization.templateFields).forEach((tfId: string) => {
        if (tfId !== 'Tenant') {
          if (Object.keys(templatization.environments[envId].settings).includes(tfId)) {
            envRef.PolicySettings[templatization.templateFields[tfId].name] = templatization.environments[envId].settings[tfId];
          }
        }
      });
    }
  });
  return JSON.stringify(appSettings, null, 2);
}

export const stringKeyOf = (value: any, ofObject: any) => {
  return value as keyof typeof ofObject;
}

/**
 * As extractTemplatizableField, except that it also keys it into an array of values if it is not templatized.
 */
export const getKeyedTemplatizableValue = (field: any, from: any, buildSettings: BuildSettings) => {
  if (!field) {
    // Let empty values just be treated as empty values.
    return field;
  }
  if ((!buildSettings.templatization) || (typeof (field) === 'string')) {
    // This is expressed in the JSON as a non-templatizable field, possibly due to being from JSON generated prior to a given field becoming templatizable.
    // For backwards compatability, just treat its value as its literal contents.
    return getKeyedValue(field, from);
  } else if (!field.templatized) {
    // The field is templatizable but marked non-templatized (uses a literal value instead.)
    return getKeyedValue(field.value, from);
  } else if (buildSettings.xmlGenerationSettings.targetEnvironment && Object.keys(buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings).includes(field.value) && (!!buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings[field.value])) {
    // The policy build is targeting a specific environment, and that environment defines a nonempty value for the setting corresponding to this templatized field.
    // Return the value from the environment definition.
    return getKeyedValue(buildSettings.templatization.environments[buildSettings.xmlGenerationSettings.targetEnvironment].settings[field.value], from);
  } else {
    // The field should be templatized. Return the template-tag format for the field.
    return `{settings:${buildSettings.templatization.templateFields[field.value].name}}`;
  }
}

export const getKeyedValue = (key: any, from: any) => {
  return from[stringKeyOf(key, from)];
}